import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import InviteHubUserModal from '../components/Modals/InviteHubUserModal'
import { usersAPI } from '../services/usersAPI'
import { organizationsAPI } from '../services/organizationsAPI'
import { rolesAPI } from '../services/rolesAPI'
import { systemAPI } from '../services/api'
import {
  Users,
  Building2,
  Shield,
  TrendingUp,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react'

export default function Dashboard() {
  const { user, isControlCenter } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showInviteHubUserModal, setShowInviteHubUserModal] = useState(false)
  
  // Debug logging
  console.log('Dashboard - User:', user)
  console.log('Dashboard - isControlCenter:', isControlCenter())

  // Fetch dashboard data based on user type - optimized with better caching
  const { data: usersData, isLoading: usersLoading } = useQuery(
    'dashboard-users',
    () => usersAPI.getStats().then(res => res.data),
    { 
      enabled: user && isControlCenter(),
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
    }
  )

  const { data: orgsData, isLoading: orgsLoading } = useQuery(
    'dashboard-organizations',
    () => organizationsAPI.getStats().then(res => res.data),
    { 
      enabled: user && isControlCenter(),
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    }
  )

  const { data: rolesData, isLoading: rolesLoading } = useQuery(
    'dashboard-roles',
    () => rolesAPI.getStats().then(res => res.data),
    { 
      enabled: user && isControlCenter(),
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
    }
  )

  const { data: activitiesData, isLoading: activitiesLoading } = useQuery(
    'dashboard-activities',
    () => usersAPI.getRecentActivities(6).then(res => res.data),
    { 
      enabled: user && isControlCenter(),
      staleTime: 2 * 60 * 1000, // 2 minutes for activities (more dynamic)
      cacheTime: 10 * 60 * 1000,
    }
  )

  const { data: systemStatusData, isLoading: systemStatusLoading } = useQuery(
    'dashboard-system-status',
    () => systemAPI.getStatus().then(res => res.data),
    { 
      enabled: user && isControlCenter(),
      staleTime: 1 * 60 * 1000, // 1 minute for system status
      refetchInterval: 30000, // Refetch every 30 seconds
      refetchOnWindowFocus: true
    }
  )

  // Show content progressively - don't wait for all data
  const isLoading = usersLoading && orgsLoading && rolesLoading && activitiesLoading && systemStatusLoading

  // Handler functions for Quick Actions
  const handleCreateUser = () => {
    navigate('/sas-users')
  }

  const handleCreateOrganization = () => {
    navigate('/organizations?create=true')
  }

  const handleManageRoles = () => {
    navigate('/roles')
  }


  const handleInviteHubUserSuccess = () => {
    setShowInviteHubUserModal(false)
    // Invalidate queries to refresh dashboard data
    queryClient.invalidateQueries('dashboard-users')
    queryClient.invalidateQueries('dashboard-activities')
  }

  const statsCards = [
    {
      title: 'Total Users',
      value: usersData?.data?.stats?.total_users || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      change: '+12%',
      changeType: 'positive'
    },
    {
      title: 'Active Users',
      value: usersData?.data?.stats?.active_users || 0,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      change: '+8%',
      changeType: 'positive'
    },
    {
      title: 'Organizations',
      value: orgsData?.data?.stats?.total_organizations || 0,
      icon: Building2,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
      change: '+5%',
      changeType: 'positive',
      path: '/organizations'
    },
    {
      title: 'Active Organizations',
      value: orgsData?.data?.stats?.active_organizations || 0,
      icon: Activity,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      change: '+3%',
      changeType: 'positive',
      path: '/organizations'
    }
  ]

  const recentActivities = activitiesData?.data?.activities || []

  // Show initial loading only if no data is available at all
  if (isLoading && !usersData && !orgsData && !rolesData && !activitiesData) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {user?.full_name || user?.username}!
        </h1>
        <p className="text-primary-100">
          {isControlCenter() 
            ? 'Here\'s what\'s happening in your control center today.'
            : 'Here\'s your organization overview.'
          }
        </p>
      </div>

      {/* Stats Grid */}
      {isControlCenter() && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statsCards.map((stat, index) => (
            <Card
              key={index}
              className={`hover:shadow-medium transition-shadow ${stat.path ? 'cursor-pointer' : ''}`}
              {...(stat.path && { onClick: () => navigate(stat.path) })}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-secondary-600 mb-1">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-secondary-900">
                      {stat.value.toLocaleString()}
                    </p>
                    <p className={`text-xs mt-1 ${
                      stat.changeType === 'positive' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change} from last month
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${stat.bgColor}`}>
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentActivities.length > 0 ? (
              <div className="space-y-4">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-50">
                    <div className={`p-2 rounded-full ${
                      activity.type === 'user' ? 'bg-blue-100' :
                      activity.type === 'organization' ? 'bg-purple-100' :
                      activity.type === 'invitation' ? 'bg-green-100' :
                      'bg-orange-100'
                    }`}>
                      {activity.type === 'user' && <Users className="h-4 w-4 text-blue-600" />}
                      {activity.type === 'organization' && <Building2 className="h-4 w-4 text-purple-600" />}
                      {activity.type === 'role' && <Shield className="h-4 w-4 text-orange-600" />}
                      {activity.type === 'invitation' && <CheckCircle className="h-4 w-4 text-green-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-secondary-900">
                        {activity.action}
                      </p>
                      <p className="text-sm text-secondary-600">
                        {activity.user}
                      </p>
                    </div>
                    <div className="text-xs text-secondary-500">
                      {activity.time}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-secondary-500">
                <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent activities</p>
                <p className="text-xs mt-1">Activities from the last 7 days will appear here</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {isControlCenter() ? (
                <>
                  <button 
                    onClick={handleCreateUser}
                    className="w-full text-left p-3 rounded-lg hover:bg-secondary-50 border border-secondary-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-600 mr-3" />
                      <div>
                        <p className="font-medium text-secondary-900">Create User</p>
                        <p className="text-sm text-secondary-600">Add new user to system</p>
                      </div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={handleCreateOrganization}
                    className="w-full text-left p-3 rounded-lg hover:bg-secondary-50 border border-secondary-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <Building2 className="h-5 w-5 text-purple-600 mr-3" />
                      <div>
                        <p className="font-medium text-secondary-900">Create Organization</p>
                        <p className="text-sm text-secondary-600">Add new organization</p>
                      </div>
                    </div>
                  </button>
                  
                  <button 
                    onClick={handleManageRoles}
                    className="w-full text-left p-3 rounded-lg hover:bg-secondary-50 border border-secondary-200 transition-colors"
                  >
                    <div className="flex items-center">
                      <Shield className="h-5 w-5 text-orange-600 mr-3" />
                      <div>
                        <p className="font-medium text-secondary-900">Manage Roles</p>
                        <p className="text-sm text-secondary-600">Update permissions</p>
                      </div>
                    </div>
                  </button>
                </>
              ) : (
                <>
                  <button className="w-full text-left p-3 rounded-lg hover:bg-secondary-50 border border-secondary-200">
                    <div className="flex items-center">
                      <Users className="h-5 w-5 text-blue-600 mr-3" />
                      <div>
                        <p className="font-medium text-secondary-900">Invite User</p>
                        <p className="text-sm text-secondary-600">Add team member</p>
                      </div>
                    </div>
                  </button>
                  
                  <button className="w-full text-left p-3 rounded-lg hover:bg-secondary-50 border border-secondary-200">
                    <div className="flex items-center">
                      <Activity className="h-5 w-5 text-green-600 mr-3" />
                      <div>
                        <p className="font-medium text-secondary-900">View Analytics</p>
                        <p className="text-sm text-secondary-600">Organization insights</p>
                      </div>
                    </div>
                  </button>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            System Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          {systemStatusData?.data?.status ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <div className={`h-3 w-3 rounded-full ${
                  systemStatusData.data.status.api_server?.status === 'operational' 
                    ? 'bg-green-500' 
                    : systemStatusData.data.status.api_server?.status === 'error'
                    ? 'bg-red-500'
                    : 'bg-yellow-500'
                }`}></div>
                <div className="flex-1">
                  <span className="text-sm text-secondary-700">API Server</span>
                  {systemStatusData.data.status.api_server?.message && (
                    <p className="text-xs text-secondary-500 mt-0.5">
                      {systemStatusData.data.status.api_server.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`h-3 w-3 rounded-full ${
                  systemStatusData.data.status.database?.status === 'operational' 
                    ? 'bg-green-500' 
                    : systemStatusData.data.status.database?.status === 'error'
                    ? 'bg-red-500'
                    : 'bg-yellow-500'
                }`}></div>
                <div className="flex-1">
                  <span className="text-sm text-secondary-700">Database</span>
                  {systemStatusData.data.status.database?.message && (
                    <p className="text-xs text-secondary-500 mt-0.5">
                      {systemStatusData.data.status.database.message}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className={`h-3 w-3 rounded-full ${
                  systemStatusData.data.status.authentication?.status === 'operational' 
                    ? 'bg-green-500' 
                    : systemStatusData.data.status.authentication?.status === 'error'
                    ? 'bg-red-500'
                    : 'bg-yellow-500'
                }`}></div>
                <div className="flex-1">
                  <span className="text-sm text-secondary-700">Authentication</span>
                  {systemStatusData.data.status.authentication?.message && (
                    <p className="text-xs text-secondary-500 mt-0.5">
                      {systemStatusData.data.status.authentication.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse"></div>
                <span className="text-sm text-secondary-700">API Server</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse"></div>
                <span className="text-sm text-secondary-700">Database</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="h-3 w-3 bg-gray-300 rounded-full animate-pulse"></div>
                <span className="text-sm text-secondary-700">Authentication</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <InviteHubUserModal
        isOpen={showInviteHubUserModal}
        onClose={() => setShowInviteHubUserModal(false)}
        onSuccess={handleInviteHubUserSuccess}
      />

    </div>
  )
}
